import React from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Typography, Box, Card, CardContent } from '@mui/material';
import CollectionItemActions from './CollectionItemActions';
import { decorateStates } from '../../helpers/decorators/StateDecorator';
import {useTranslation} from "react-i18next";

const CollectionItemModal = ({
                          openReserve,
                          isLoading,
                          handleCloseReserve,
                          title,
                          author,
                          imageUrl,
                          publisher,
                          states,
                          description,
                          availability,
                          coverImage, // ✅ Add book cover
                          isBorrowedMe,
                          isBorrowed,
                          isHolded,
                          isHoldedMe,
                          isReserved,
                          isReservedMe,
                          handleCancelReserve,
                          handleReserve,
                          handleHold,
                          handleCancelHold
                      }) => {
    const { t } = useTranslation();

    return (
        <Dialog fullScreen open={openReserve} onClose={handleCloseReserve} aria-labelledby="reserve-title">
            <DialogTitle
              id="reserve-title"
              sx={{
                background: 'linear-gradient(315deg, #6f923c 0%, #7ea73f 50%, #4f6a23 100%)',
                boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
                color: '#f3f6ec',
                textShadow: '0 1px 2px rgba(0,0,0,0.35)',
                textAlign: 'center',
                py: 2,
                px: 2,
              }}
            >
                {t('catalog.item.details')}: {title}
            </DialogTitle>

            <DialogContent
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                px: { xs: 2, sm: 3 },
                py: { xs: 2, sm: 3 },
                gap: 2,
                backgroundColor: '#f7f7fb',
              }}
            >
                {/* ✅ Book Cover */}
                <Card
                  sx={{
                    display: 'flex',
                    flexDirection: { xs: 'column', sm: 'row' },
                    alignItems: 'stretch',
                    width: '100%',
                    maxWidth: 720,
                    borderRadius: 3,
                    overflow: 'hidden',
                    backgroundColor: 'white',
                    border: '1px solid rgba(0,0,0,0.08)',
                    boxShadow: '0 10px 30px rgba(20, 20, 40, 0.10)',
                  }}
                >
                    <Box
                      sx={{
                        display: { xs: 'none', sm: 'flex' },
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 200,
                        minHeight: 280,
                        p: 2,
                        backgroundColor: 'rgba(107, 91, 149, 0.06)',
                        borderRight: '1px solid rgba(0,0,0,0.06)',
                      }}
                    >
                      <Box
                        sx={{
                          width: 160,
                          height: 240,
                          borderRadius: 2,
                          overflow: 'hidden',
                          boxShadow: '0 8px 18px rgba(20, 20, 40, 0.18)',
                          backgroundColor: '#fff',
                        }}
                      >
                        <img
                          src={imageUrl}
                          alt={title}
                          className="item-image"
                          style={{
                            display: 'block',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                        />
                      </Box>
                    </Box>
                    <Box
                      sx={{
                        p: { xs: 2, sm: 3 },
                        textAlign: { xs: 'center', sm: 'left' },
                        flex: 1,
                      }}
                    >
                      <CardContent sx={{ p: 0 }}>
                        <Typography variant="h5" sx={{ fontWeight: 800, color: '#222', mb: 1.5, lineHeight: 1.15 }}>
                            {title}
                        </Typography>

                        <Typography variant="body1" sx={{ color: '#555', marginBottom: '8px' }}>
                            <strong>{t('catalog.item.author')}:</strong> {author}
                        </Typography>

                        {description && (
                          <>
                            <Typography variant="body1" sx={{ color: '#555', marginBottom: '4px' }}>
                                <strong>{t('catalog.item.description')}:</strong>
                            </Typography>

                            <Box
                                sx={{
                                    maxHeight: '120px',
                                    overflowY: 'auto',
                                    border: '1px solid #ddd',
                                    padding: '8px',
                                    borderRadius: '4px',
                                    fontSize: '14px',
                                    color: '#555',
                                    marginBottom: '12px',
                                    whiteSpace: 'pre-wrap',
                                    backgroundColor: '#fafafa',
                                }}
                            >
                                {description}
                            </Box>
                          </>
                        )}

                        <Typography variant="body1" sx={{ color: '#555', marginBottom: '8px' }}>
                            <strong>{t('catalog.item.publisher')}:</strong> {publisher}
                        </Typography>

                        <Typography
                            variant="body1"
                            sx={{
                                fontWeight: 'bold',
                                color: (availability || decorateStates({ states, isReservedMe, isHoldedMe }) === 'available')  ? 'green' : 'red',
                                marginTop: '8px',
                            }}
                        >
                            {t(`catalog.item.status.${decorateStates({ states, isReservedMe, isHoldedMe })}`)}
                        </Typography>
                      </CardContent>
                    </Box>
                </Card>

                {/* ✅ Action Buttons */}
                <Box
                  sx={{
                    mt: 2,
                    width: '100%',
                    maxWidth: 720,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CollectionItemActions
                      isLoading={isLoading}
                      onCancelReserve={handleCancelReserve}
                      onReserve={handleReserve}
                      onHold={handleHold}
                      onCancelHold={handleCancelHold}
                      isBorrowedMe={isBorrowedMe}
                      isBorrowed={isBorrowed}
                      isHolded={isHolded}
                      isHoldedMe={isHoldedMe}
                      isReserved={isReserved}
                      isReservedMe={isReservedMe}
                  />
                </Box>

                {/* ✅ Close Button (Now Aligned with Actions) */}
                <Button
                  onClick={handleCloseReserve}
                  variant="outlined"
                  color="secondary"
                  sx={{ marginTop: '5px', width: '250px', borderRadius: '8px' }}
                >
                    {t('close')}
                </Button>
            </DialogContent>
        </Dialog>
    );
};

export default CollectionItemModal;
